import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from "@/composables/useNotifications";
import i18n from '@/libs/i18n';
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'

export default function usePhysicalLocationList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { handleError } = useCommon()
  const { updateItem, getItems, updateItems } = useLambda()
  const userData = store.state?.userStore?.userData
  const clientId = userData.role !== "admin" ? userData.client.$oid : null
  const physicalLocations = ref([])
  const collection = 'physical_location'

  // Table Headers
  const tableColumns = computed(() => {
    return [
      { key: 'name', label: i18n.t('message.tableHeader.physical_locations'), sortable: true },
      { key: 'Actions', label: i18n.t('message.tableHeader.actions') },
    ]
  })

  const totalPhysicalLocations = ref(0)
  const perPage = ref(10)
  const currentPage = ref(1)
  const perPageOptions = [5, 10, 15, 20, 25, 30, 40, 50, 100]
  const searchQuery = ref('')
  const defaultSortBy = 'name'
  const sortBy = ref(defaultSortBy)
  const isSortDirDesc = ref(false)
  const isLoading = ref(true)

  const fetchPhysicalLocations = async (searchValue = '') => {
    isLoading.value = true

    try {

      const query = {
        client_id: oid(clientId),
        deleted: { $ne: true },
      }

      const items = await getItems({ collection, query })
      if (!items) return
  
      // Search
      if (searchValue && searchValue !== searchQuery.value) return

      physicalLocations.value  = searchQuery.value ? items.filter(e => e.name?.toLowerCase().includes(searchQuery.value.toLowerCase())) : items
      totalPhysicalLocations.value = physicalLocations.value.length
    
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: i18n.t('message.physical_location_list_fetch_error') })
    } finally {
      isLoading.value = false
    }
  }

  const searchPhysicalLocation = searchValue => {
    isLoading.value = true
    fetchPhysicalLocations(searchValue)
  }

  const deletePhysicalLocation = async (id) => {
    try {
      const query = { _id: oid(id) }
      const action = { $set: { deleted: true } }

      await updateItem({ collection, query, action })

      const queryLocation = { client_id: oid(clientId), physical_location: oid(id) }
      const actionLocation = { $set: { physical_location: null } }
      await updateItems({ collection: 'location', query: queryLocation, action: actionLocation })

      fetchPhysicalLocations()
      showSuccessMessage(i18n.t('message.physical_location_remove'))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.something_went_wrong'))
    }
  }

  const showingMessage = computed(() => {
    const from = ((currentPage.value * perPage.value) - perPage.value) + (physicalLocations.value.length ? 1 : 0)
    const to = physicalLocations.value.length + ((currentPage.value * perPage.value) - perPage.value)
    return i18n.tc('message.paginationText', 0, { from: from, to: to, total: totalPhysicalLocations.value })
  })

  watch([currentPage, perPage], () => {
    fetchPhysicalLocations()
  })

  watch([searchQuery, sortBy, isSortDirDesc], () => {
    currentPage.value = 1
    fetchPhysicalLocations()
  })

  return {
    fetchPhysicalLocations,
    tableColumns,
    perPage,
    currentPage,
    perPageOptions,
    totalPhysicalLocations,
    showingMessage,
    searchQuery,
    sortBy,
    isSortDirDesc,
    physicalLocations,
    deletePhysicalLocation,
    isLoading,
    searchPhysicalLocation
  }
}